<script setup lang="ts">
  import { computed } from 'vue';

  import BvTimer from '@bv/components/timer/BvTimer.vue';
  import { type Call } from '@bv/stores';
  import { trans } from '@bv/utils';

  import CallActions from './../partials/actions/CallActions.vue';
  import CallerAvatar from './../partials/icons/CallerAvatar.vue';
  import CallIntersectionActions from './../partials/intersection-actions/CallIntersectionActions.vue';

  const props = withDefaults(
    defineProps<{
      call: Call;
      previousCall?: Call;
      hideIntersectionActions?: boolean;
      actionSize?: 'sm';
      showTransfer?: boolean;
      showAvatar?: boolean;
    }>(),
    {
      previousCall: undefined,
      hideIntersectionActions: false,
      actionSize: undefined,
      showTransfer: false,
      showAvatar: false,
    },
  );

  const callDetails = computed<Call>(() => {
    if (props.previousCall && props.hideIntersectionActions && props.call.status === 'ringing') {
      return props.previousCall;
    }
    return props.call;
  });

  const emit = defineEmits<{
    (event: 'on-transfer'): void;
  }>();

  /**
   * Emits an 'on-transfer' event.
   * This function notifies a parent component that the user has initiated a transfer action.
   * @returns {void}
   */
  const transfer = (): void => {
    emit('on-transfer');
  };
</script>

<template>
  <div class="flex flex-col gap-2.5">
    <caller-avatar
      v-if="showAvatar"
      class="m-auto mb-1.5 h-40 w-40"
    />
    <p
      v-if="callDetails.endCallReason || callDetails.isCanceled || callDetails.status === 'Terminated'"
      class="min-h-14 text-center text-xl leading-5 font-bold text-red-500"
    >
      {{ trans(`voip.end_call_reason.${callDetails.endCallReason ? 'canceled' : 'ended'}_trans`) }}
    </p>
    <div
      v-else
      class="flex min-h-14 flex-col gap-1 text-center leading-4"
    >
      <div class="text-white">
        {{ callDetails.caller.name || callDetails.caller.number }}
      </div>
      <div class="text-gray-200">
        {{ callDetails.caller.name ? callDetails.caller.number : '' }}
      </div>
      <div class="text-green-500">
        <template v-if="callDetails.status === 'ringing'">
          {{ trans('voip.calls.incoming_call_trans') }}
        </template>
        <div
          v-else-if="callDetails.status && ['calling', 'Establishing'].includes(callDetails.status)"
          class="dots-after"
        >
          {{ trans('voip.calls.status.calling_trans') }}
        </div>
        <bv-timer
          v-else
          :start-time="callDetails.cdr.startTimeAnswer"
        />
      </div>
    </div>
    <call-intersection-actions
      v-if="!hideIntersectionActions && callDetails.status === 'ringing' && previousCall"
      :call
      :previous-call
    />
    <call-actions
      v-else-if="callDetails.status !== 'Terminated'"
      :call="callDetails"
      :previous-call
      :show-transfer
      :size="actionSize"
      @on-transfer="transfer"
    />
  </div>
</template>
